import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AppNotification,
  NotificationApiResponse,
  NotificationFilter,
  NotificationPage,
} from '../models/notification.model';

interface NotificationPageApiResponse {
  notifications: NotificationApiResponse[];
  next_cursor: string | null;
  unread_total: number;
  unread_chat_total: number;
}

interface UnreadTotalsApiResponse {
  unread_total: number;
  unread_chat_total: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly apiUrl = `${environment.apiUrl}/notifications`;
  private readonly http = inject(HttpClient);
  private readonly unreadTotalSubject = new BehaviorSubject(0);
  private readonly unreadChatTotalSubject = new BehaviorSubject(0);

  readonly unreadTotal$ = this.unreadTotalSubject.asObservable();
  readonly unreadChatTotal$ = this.unreadChatTotalSubject.asObservable();

  getNotifications(cursor: string | null = null, perPage = 15, filter: NotificationFilter = 'all'): Observable<NotificationPage> {
    let params = new HttpParams()
      .set('per_page', perPage)
      .set('filter', filter);

    if (cursor !== null) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<NotificationPageApiResponse>(this.apiUrl, { params }).pipe(
      map(response => ({
        notifications: response.notifications.map(notification => this.mapNotification(notification)),
        nextCursor: response.next_cursor,
        unreadTotal: response.unread_total,
        unreadChatTotal: response.unread_chat_total,
      })),
      tap(response => this.updateUnreadTotals(response.unreadTotal, response.unreadChatTotal)),
    );
  }

  markRead(id: string): Observable<number> {
    return this.http.post<UnreadTotalsApiResponse>(`${this.apiUrl}/read`, { id }).pipe(
      tap(response => this.updateUnreadTotals(response.unread_total, response.unread_chat_total)),
      map(response => response.unread_total),
    );
  }

  markAllRead(): Observable<number> {
    return this.http.post<UnreadTotalsApiResponse>(`${this.apiUrl}/read`, { all: true }).pipe(
      tap(response => this.updateUnreadTotals(response.unread_total, response.unread_chat_total)),
      map(response => response.unread_total),
    );
  }

  markAllChatsRead(): Observable<number> {
    return this.http.post<UnreadTotalsApiResponse>(`${this.apiUrl}/read`, { chats: true }).pipe(
      tap(response => this.updateUnreadTotals(response.unread_total, response.unread_chat_total)),
      map(response => response.unread_total),
    );
  }

  markTradeRead(tradeId: number): Observable<number> {
    return this.http.post<UnreadTotalsApiResponse>(`${this.apiUrl}/read`, { trade_id: tradeId }).pipe(
      tap(response => this.updateUnreadTotals(response.unread_total, response.unread_chat_total)),
      map(response => response.unread_total),
    );
  }

  refreshUnreadTotal(): Observable<NotificationPage> {
    return this.getNotifications(null, 1, 'all');
  }

  private updateUnreadTotals(unreadTotal: number, unreadChatTotal: number): void {
    this.unreadTotalSubject.next(unreadTotal);
    this.unreadChatTotalSubject.next(unreadChatTotal);
  }

  private mapNotification(notification: NotificationApiResponse): AppNotification {
    return {
      id: notification.id,
      type: notification.type,
      filterGroup: notification.filter_group,
      title: notification.title,
      body: notification.body,
      icon: notification.icon,
      route: notification.route,
      isRead: notification.is_read,
      createdAt: notification.created_at,
    };
  }
}
